import React, { useEffect, useState } from "react";

import Constants from "../Constants";

// Build identity, baked at compile time by the package.json build script — CRA
// only exposes REACT_APP_* vars, so the version (from the repo-root VERSION
// file) and git SHA (Render's RENDER_GIT_COMMIT) are injected there. Mirrors the
// web-enterprise badge:
//   admin = THIS dashboard's own build.
//   api   = the backend it's wired to, fetched from /api/v1/info — doubles as a
//           live check of which backend REACT_APP_API_BASE_URL points at.
// Click the badge to copy the full identity (for bug reports).
const ADMIN_VERSION = process.env.REACT_APP_VERSION || "0.0.0";
const ADMIN_SHA = (process.env.REACT_APP_GIT_SHA || "dev").slice(0, 12);

const buttonStyle = {
  fontSize: "12px",
  color: "#6b7280",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  cursor: "pointer",
};
// The git SHA is fine print for pinning the exact build — smaller + lighter than
// the human version QA reads out loud.
const shaStyle = { fontSize: "0.85em", opacity: 0.6, marginLeft: "2px" };

const BuildTag = ({ label, version, sha }) => (
  <span>
    {label} {version}
    <span style={shaStyle}>+{sha}</span>
  </span>
);

// Copy the full build identity — prefer navigator.clipboard, fall back to a
// hidden textarea for non-secure contexts / old browsers.
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } catch (e2) {
      /* best effort */
    }
    document.body.removeChild(ta);
  }
};

const VersionBadge = () => {
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${Constants.API_BASE_URL}/api/v1/info`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (active) setInfo(data);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const apiLabel = info
    ? `api ${info.version}+${info.git_sha}`
    : failed
      ? "api ?"
      : "api …";

  // What lands on the clipboard for a bug report: the build identity line, plus
  // deployed_at + min_app_version (otherwise only in the tooltip) once loaded.
  const copyText = info
    ? `admin ${ADMIN_VERSION}+${ADMIN_SHA} · ${apiLabel}\n` +
      `deployed_at: ${info.deployed_at ?? "unknown"} · ` +
      `min_app_version: ${info.min_app_version ?? "unknown"}`
    : `admin ${ADMIN_VERSION}+${ADMIN_SHA} · ${apiLabel}`;

  const handleCopy = async () => {
    await copyToClipboard(copyText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  const tooltip = info
    ? `deployed_at: ${info.deployed_at ?? "unknown"}\n` +
      `min_app_version: ${info.min_app_version ?? "unknown"}\n\n` +
      "Click to copy"
    : "Click to copy";

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={tooltip}
      aria-label="Copy build version info"
      style={buttonStyle}
    >
      {copied ? (
        <span style={{ fontWeight: 600, color: "#059669" }}>Copied ✓</span>
      ) : (
        <>
          <BuildTag label="admin" version={ADMIN_VERSION} sha={ADMIN_SHA} />
          <span aria-hidden="true"> · </span>
          {info ? (
            <BuildTag label="api" version={info.version} sha={info.git_sha} />
          ) : (
            <span>{failed ? "api ?" : "api …"}</span>
          )}
          {info && info.staging ? (
            <span
              style={{ marginLeft: "4px", fontWeight: 600, color: "#d97706" }}
            >
              staging
            </span>
          ) : null}
        </>
      )}
    </button>
  );
};

export default VersionBadge;
